<!--
  TiptapEditorPrototype.vue -- CKEditor 5 -> TipTap evaluation prototype.

  THROWAWAY PROTOTYPE, NOT PRODUCTION. Self-contained page that stands up
  the FULL target rich-text editor we would ship if we swap CKEditor 5
  (GPL-2.0) for TipTap (MIT). It exists to de-risk that swap BEFORE we
  touch the 3 live CKEditor sites -- especially Jake's "no second swap"
  constraint: prove inline images work against our REAL upload endpoint,
  in the same editor, so we never have to swap again to get images.

  Design doc: docs/planning/2026-08-04-ckeditor-to-tiptap-migration.md

  What it demonstrates:
    * A TipTap editor (Vue 3 binding, @tiptap/vue-3) wired with the full
      target toolbar: a block-format dropdown (Paragraph / H1 / H2 / H3),
      bold, italic, link (add/edit/remove), bulleted + numbered lists,
      blockquote, undo/redo, AND inline image insert (gated behind a
      prototype-local "Enable inline images" toggle, off by default).
    * HTML-native content: the editor is seeded from an HTML string and
      read back via editor.getHTML() -- exactly the interface production
      would use (HTML in, sanitized HTML stored). TipTap's internal model
      is ProseMirror JSON, but HTML in/out is first-class; we deliberately
      stay HTML-native to keep the content model editor-agnostic.
    * A LIVE HTML-output pane showing editor.getHTML() so a human can
      eyeball the emitted markup that would get stored + sanitized.
    * Inline images wired to the REAL endpoint: POST /api/upload_image
      (multipart, field name `file`) -> response.data.uuid -> insert an
      <img src> pointing at getImageBaseUrl() + uuid. This mirrors the
      productEditor store's _uploadArtwork exactly (same endpoint, same
      Content-Type: undefined so axios derives the multipart boundary).

  PRODUCTION NOTE (shown in the UI too): the emitted HTML would be stored
  through nh3 (backend) / DOMPurify (render) in production. Those
  allowlists do NOT permit <img> today -- the real migration must widen
  them (src limited to our uploaded-asset origin). This prototype does NOT
  sanitize; it just shows the raw getHTML().

  Gated superadmin.settings; reachable from the SuperAdmin Developer
  (Prototypes) section and by direct URL
  (/admin/prototypes/tiptap-editor).
-->
<template>
  <div class="tiptap-proto" data-testid="tiptap-prototype">
    <header class="tiptap-proto__head">
      <h1 class="tiptap-proto__title">
        TipTap Rich-Text Editor -- CKEditor replacement eval
      </h1>
      <p class="tiptap-proto__sub">
        Full target editor for the CKEditor 5 (GPL) -&gt; TipTap (MIT) swap.
        HTML-native in/out, the complete toolbar, and inline images wired to
        the real
        <code>POST /api/upload_image</code>
        endpoint -- proof the swap needs no second swap to add images later.
      </p>
    </header>

    <!-- Prototype-local "Text Editor settings" MOCK card. This is a preview
         of where the real SuperAdmin setting will live; it is NOT wired to a
         backend and does NOT persist. It exists so Jake can evaluate the
         image-enabled / image-disabled states from a settings-style control,
         driving the component-local `inlineImagesEnabled` flag only. -->
    <section
      class="tiptap-proto__settings"
      data-testid="sa-proto-texteditor-card"
    >
      <div class="tiptap-proto__settings-head">
        <h2 class="tiptap-proto__settings-title">Text Editor settings</h2>
        <span class="tiptap-proto__settings-badge">Prototype preview</span>
      </div>
      <label class="tiptap-proto__settings-row">
        <input
          type="checkbox"
          data-testid="tiptap-proto-enable-images-toggle"
          :checked="inlineImagesEnabled"
          @change="inlineImagesEnabled = $event.target.checked"
        />
        <span class="tiptap-proto__settings-label">Enable inline images</span>
      </label>
      <p class="tiptap-proto__settings-note">
        Prototype preview -- in production this lives on the SuperAdmin Settings
        page as a persisted platform setting; not wired to the backend yet.
      </p>
    </section>

    <div class="tiptap-proto__body">
      <!-- EDITOR + toolbar -->
      <section class="tiptap-proto__editor-col">
        <div
          v-if="editor"
          class="tiptap-proto__toolbar"
          data-testid="tiptap-toolbar"
        >
          <!-- Block-format dropdown: reflects the current block the cursor
               is in and applies Paragraph / Heading 1 / Heading 2 / Heading 3
               on change. Replaces the former H2/H3 text buttons. -->
          <select
            class="tiptap-proto__block-select"
            data-testid="tiptap-block-format"
            title="Block format"
            :value="currentBlockFormat"
            @change="onBlockFormatChange($event.target.value)"
          >
            <option value="paragraph">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>

          <span class="tiptap-proto__sep" aria-hidden="true"></span>

          <button
            type="button"
            class="tiptap-proto__tbtn"
            :class="{ 'is-active': editor.isActive('bold') }"
            data-testid="tiptap-btn-bold"
            title="Bold"
            @click="chain().toggleBold().run()"
          >
            <span class="material-icons">format_bold</span>
          </button>
          <button
            type="button"
            class="tiptap-proto__tbtn"
            :class="{ 'is-active': editor.isActive('italic') }"
            data-testid="tiptap-btn-italic"
            title="Italic"
            @click="chain().toggleItalic().run()"
          >
            <span class="material-icons">format_italic</span>
          </button>

          <span class="tiptap-proto__sep" aria-hidden="true"></span>

          <button
            type="button"
            class="tiptap-proto__tbtn"
            :class="{ 'is-active': editor.isActive('bulletList') }"
            data-testid="tiptap-btn-bullet-list"
            title="Bulleted list"
            @click="chain().toggleBulletList().run()"
          >
            <span class="material-icons">format_list_bulleted</span>
          </button>
          <button
            type="button"
            class="tiptap-proto__tbtn"
            :class="{ 'is-active': editor.isActive('orderedList') }"
            data-testid="tiptap-btn-ordered-list"
            title="Numbered list"
            @click="chain().toggleOrderedList().run()"
          >
            <span class="material-icons">format_list_numbered</span>
          </button>

          <span class="tiptap-proto__sep" aria-hidden="true"></span>

          <button
            type="button"
            class="tiptap-proto__tbtn"
            :class="{ 'is-active': editor.isActive('blockquote') }"
            data-testid="tiptap-btn-blockquote"
            title="Blockquote"
            @click="chain().toggleBlockquote().run()"
          >
            <span class="material-icons">format_quote</span>
          </button>

          <span class="tiptap-proto__sep" aria-hidden="true"></span>

          <button
            type="button"
            class="tiptap-proto__tbtn"
            :class="{ 'is-active': editor.isActive('link') }"
            data-testid="tiptap-btn-link"
            title="Add / edit link"
            @click="onSetLink"
          >
            <span class="material-icons">link</span>
          </button>
          <button
            type="button"
            class="tiptap-proto__tbtn"
            data-testid="tiptap-btn-unlink"
            title="Remove link"
            :disabled="!editor.isActive('link')"
            @click="chain().unsetLink().run()"
          >
            <span class="material-icons">link_off</span>
          </button>

          <span class="tiptap-proto__sep" aria-hidden="true"></span>

          <button
            type="button"
            class="tiptap-proto__tbtn"
            data-testid="tiptap-btn-undo"
            title="Undo"
            :disabled="!editor.can().undo()"
            @click="chain().undo().run()"
          >
            <span class="material-icons">undo</span>
          </button>
          <button
            type="button"
            class="tiptap-proto__tbtn"
            data-testid="tiptap-btn-redo"
            title="Redo"
            :disabled="!editor.can().redo()"
            @click="chain().redo().run()"
          >
            <span class="material-icons">redo</span>
          </button>

          <span
            v-if="inlineImagesEnabled"
            class="tiptap-proto__sep"
            aria-hidden="true"
          ></span>

          <button
            v-if="inlineImagesEnabled"
            type="button"
            class="tiptap-proto__tbtn tiptap-proto__tbtn--primary"
            data-testid="tiptap-btn-image"
            title="Insert image (uploads to /api/upload_image)"
            :disabled="uploading"
            @click="openImagePicker"
          >
            <span
              v-if="uploading"
              class="material-icons tiptap-proto__spin"
            >hourglass_top</span>
            <span v-else class="material-icons">image</span>
          </button>
        </div>

        <!-- The contenteditable surface TipTap owns. -->
        <editor-content
          v-if="editor"
          :editor="editor"
          class="tiptap-proto__surface"
          data-testid="tiptap-editor"
        />

        <!-- Hidden native file input the Image button drives. Accepts web
             image types only (validated again in JS via the shared
             artwork-upload contract, image subset). -->
        <input
          ref="imageInput"
          type="file"
          class="tiptap-proto__file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
          data-testid="tiptap-image-input"
          @change="onImageInputChange"
        />

        <p
          v-if="uploadError"
          class="tiptap-proto__error"
          data-testid="tiptap-upload-error"
        >
          {{ uploadError }}
        </p>

        <p class="tiptap-proto__hint">
          Inline images upload to
          <code>POST /api/upload_image</code>
          (field <code>file</code>) and insert an
          <code>&lt;img src&gt;</code>
          built from
          <code>getImageBaseUrl() + uuid</code>.
          You can also paste or drop an image directly into the editor.
          Inline images are toggled from the
          <strong>Text Editor settings</strong> card above (prototype-local;
          off by default).
        </p>
      </section>

      <!-- LIVE HTML OUTPUT -->
      <section class="tiptap-proto__output-col">
        <div class="tiptap-proto__output-head">
          <h2 class="tiptap-proto__output-title">
            Live HTML output (<code>editor.getHTML()</code>)
          </h2>
          <p class="tiptap-proto__output-note">
            This is exactly what production would store and then sanitize
            (nh3 backend / DOMPurify render). Those allowlists do NOT permit
            <code>&lt;img&gt;</code> today -- the real migration must widen
            them, restricting <code>src</code> to our own uploaded-asset
            origin.
          </p>
        </div>
        <pre
          class="tiptap-proto__output"
          data-testid="tiptap-html-output"
        >{{ html }}</pre>
      </section>
    </div>

    <!-- Link editor dialog. Replaces window.prompt: a URL input, an
         "open in new tab" checkbox (unchecked by default), and Save/Cancel.
         Opened by the toolbar link button or Mod-K; prefills the current
         link's href when editing, or a clipboard URL when adding new. -->
    <div
      v-if="linkDialog.open"
      class="tiptap-proto__dialog-overlay"
      data-testid="tiptap-link-dialog"
      @click.self="closeLinkDialog"
      @keydown.esc.stop.prevent="closeLinkDialog"
    >
      <div
        class="tiptap-proto__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tiptap-link-dialog-title"
      >
        <h2 id="tiptap-link-dialog-title" class="tiptap-proto__dialog-title">
          {{ linkDialog.editing ? 'Edit link' : 'Add link' }}
        </h2>
        <form @submit.prevent="saveLinkDialog">
          <label class="tiptap-proto__dialog-label" for="tiptap-link-url">
            Link URL
          </label>
          <input
            id="tiptap-link-url"
            ref="linkUrlInput"
            v-model="linkDialog.url"
            type="text"
            class="tiptap-proto__dialog-input"
            data-testid="tiptap-link-url-input"
            placeholder="https://example.com"
            @keydown.esc.stop.prevent="closeLinkDialog"
          />
          <label class="tiptap-proto__dialog-checkbox">
            <input
              v-model="linkDialog.newTab"
              type="checkbox"
              data-testid="tiptap-link-newtab-checkbox"
            />
            <span>Open in new tab/window</span>
          </label>
          <div class="tiptap-proto__dialog-actions">
            <button
              type="button"
              class="tiptap-proto__dialog-btn"
              data-testid="tiptap-link-cancel"
              @click="closeLinkDialog"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="tiptap-proto__dialog-btn tiptap-proto__dialog-btn--primary"
              data-testid="tiptap-link-save"
            >
              {{ linkDialog.editing ? 'Save' : 'Add' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'

import api from '@/services/api'
import { getImageBaseUrl } from '@/utils/imageBaseUrl'

// Web image types this prototype accepts for inline insert. A tighter,
// image-only subset of the shared artwork contract (which also allows
// PDFs / embroidery files that are not <img>-renderable).
const IMAGE_MIME_ALLOWLIST = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
]
// Prototype-local size cap (10 MB) -- generous for a web image, well
// under the backend's 50 MB artwork cap.
const IMAGE_MAX_BYTES = 10 * 1024 * 1024

// rel applied to new-tab links: prevents the opened page from reaching back
// through window.opener (noopener/noreferrer) and marks the link nofollow.
const NEW_TAB_REL = 'noopener noreferrer nofollow'
// rel for same-tab links: no need for noopener (no new window), keep nofollow.
const SAME_TAB_REL = 'nofollow'
// Clipboard values we will best-effort prefill into a NEW link's URL field.
const CLIPBOARD_URL_RE = /^(https?:\/\/|mailto:)/i

// Seed content demonstrating the full HTML tag set the editor emits, so
// the output pane is populated on first load.
const SEED_HTML = [
  '<h2>Welcome to your store</h2>',
  '<p>This is a <strong>TipTap</strong> editor emitting <em>HTML-native</em> ',
  'content. Try the toolbar, add a <a href="https://threadflow.com">link</a>, ',
  'or insert an image.</p>',
  '<ul><li>Bulleted lists work</li><li>So do numbered lists</li></ul>',
  '<blockquote><p>Blockquotes too.</p></blockquote>',
].join('')

export default {
  name: 'TiptapEditorPrototype',

  components: { EditorContent },

  data() {
    return {
      editor: null,
      // Mirror of editor.getHTML(), kept live via the editor's update
      // event so the output pane and any test can read it reactively.
      html: '',
      uploading: false,
      uploadError: null,
      // Bumped on every editor selection/transaction so the toolbar's
      // can()-gated buttons (undo/redo) re-evaluate.
      selectionTick: 0,
      // PROTOTYPE-LOCAL flag (NOT a backend platform setting). Gates whether
      // the inline-image toolbar button is enabled. Default false so Jake
      // evaluates the disabled state first; flipped by the mock "Text Editor
      // settings" card. The real setting is deferred to the migration phase.
      inlineImagesEnabled: false,
      // Link-editor dialog state (replaces window.prompt). `open` gates the
      // overlay; `url` binds the text input; `newTab` binds the checkbox
      // (unchecked -> same tab, no target); `editing` distinguishes edit vs.
      // add for labels + clipboard-prefill suppression.
      linkDialog: {
        open: false,
        url: '',
        newTab: false,
        editing: false,
      },
    }
  },

  computed: {
    // Reflects the block the cursor is currently in so the block-format
    // dropdown shows the right label. Depends on selectionTick so it
    // recomputes as the cursor moves between paragraphs / headings.
    currentBlockFormat() {
      // eslint-disable-next-line no-unused-expressions
      this.selectionTick
      if (!this.editor) return 'paragraph'
      if (this.editor.isActive('heading', { level: 1 })) return 'h1'
      if (this.editor.isActive('heading', { level: 2 })) return 'h2'
      if (this.editor.isActive('heading', { level: 3 })) return 'h3'
      return 'paragraph'
    },
  },

  mounted() {
    // Captured so the keyboard-shortcuts extension's handlers reach the
    // component's dialog-open / unlink methods.
    const vm = this
    // Cmd/Ctrl-K opens the link dialog (same flow as the toolbar button);
    // Cmd/Ctrl-Shift-K removes the link at the cursor. `Mod` maps to Cmd on
    // Mac and Ctrl elsewhere. Both return true to signal the key is handled.
    const LinkShortcuts = Extension.create({
      name: 'tiptapProtoLinkShortcuts',
      addKeyboardShortcuts() {
        return {
          'Mod-k': () => {
            vm.openLinkDialog()
            return true
          },
          'Mod-Shift-k': () => {
            vm.removeLinkAtCursor()
            return true
          },
        }
      },
    })

    this.editor = new Editor({
      content: SEED_HTML,
      extensions: [
        LinkShortcuts,
        // StarterKit bundles paragraph, headings, bold, italic, lists,
        // blockquote, history (undo/redo), and a Link extension. We disable
        // its bundled Link so our explicitly-configured Link (below) is the
        // single link extension (no duplicate-extension warning).
        StarterKit.configure({
          link: false,
        }),
        // No HTMLAttributes here: target/rel are applied PER LINK from the
        // dialog (setLink) so the "open in new tab" checkbox actually
        // controls them. A blanket HTMLAttributes: { target, rel } would
        // force every link to new-tab regardless of the checkbox.
        Link.configure({
          openOnClick: false,
          autolink: true,
        }),
        // Inline images. `inline: false` -> block <img> (matches how a
        // welcome message / category description would embed one). We do
        // NOT enable base64 -- images go through our upload endpoint and
        // are referenced by served URL only.
        Image.configure({
          inline: false,
          allowBase64: false,
        }),
      ],
      onUpdate: ({ editor }) => {
        this.html = editor.getHTML()
      },
      onSelectionUpdate: () => {
        this.selectionTick += 1
      },
      onTransaction: () => {
        this.selectionTick += 1
      },
      // Paste / drop an image directly into the editor -> same upload path.
      editorProps: {
        handlePaste: (view, event) => this.handleClipboard(event),
        handleDrop: (view, event) => this.handleDropEvent(event),
      },
    })
    this.html = this.editor.getHTML()
  },

  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  },

  methods: {
    // Convenience: a focused command chain. Every toolbar action funnels
    // through here so focus is restored before the command runs.
    chain() {
      return this.editor.chain().focus()
    },

    // Apply the block format chosen in the dropdown. Paragraph clears any
    // heading; Heading 1/2/3 set that level. setParagraph/setHeading are
    // idempotent (unlike toggleHeading) so re-selecting the current value
    // is a no-op rather than a toggle-off.
    onBlockFormatChange(value) {
      if (value === 'h1') {
        this.chain().setHeading({ level: 1 }).run()
      } else if (value === 'h2') {
        this.chain().setHeading({ level: 2 }).run()
      } else if (value === 'h3') {
        this.chain().setHeading({ level: 3 }).run()
      } else {
        this.chain().setParagraph().run()
      }
    },

    // Toolbar link button -> open the dialog (same entry point as Mod-K).
    onSetLink() {
      this.openLinkDialog()
    },

    // Open the link editor dialog, prefilling its fields:
    //  * On an existing link -> href + newTab reflect that link's attrs.
    //  * On a new link -> best-effort clipboard URL (guarded), else empty.
    // Async only so the clipboard read can be awaited; a clipboard failure
    // never blocks the dialog (it opens empty).
    async openLinkDialog() {
      if (!this.editor) return
      const attrs = this.editor.getAttributes('link')
      const editing = !!attrs.href
      let url = editing ? attrs.href : ''
      const newTab = editing ? attrs.target === '_blank' : false

      // Clipboard prefill applies to NEW links only; an existing link's href
      // wins and is never overridden by the clipboard.
      if (!editing) {
        url = await this.readClipboardUrl()
      }

      this.linkDialog.url = url
      this.linkDialog.newTab = newTab
      this.linkDialog.editing = editing
      this.linkDialog.open = true

      // Autofocus the URL field once the overlay renders.
      await this.$nextTick()
      const input = this.$refs.linkUrlInput
      if (input && typeof input.focus === 'function') input.focus()
    },

    // Best-effort clipboard read for the new-link URL prefill. Guarded on
    // every axis: the API may be absent, readText may reject (permissions),
    // or the value may not be a URL. Any of those -> return '' (open empty);
    // a clipboard failure must never crash or block the dialog.
    async readClipboardUrl() {
      try {
        const clip = navigator?.clipboard
        if (!clip || typeof clip.readText !== 'function') return ''
        const text = await clip.readText()
        if (typeof text === 'string' && CLIPBOARD_URL_RE.test(text.trim())) {
          return text.trim()
        }
      } catch {
        // Permission denied / unavailable / not focused -- ignore.
      }
      return ''
    },

    closeLinkDialog() {
      this.linkDialog.open = false
    },

    // Apply the link from the dialog. Empty URL -> remove the link (mirrors
    // the old prompt's empty-string behavior). target/rel are set PER LINK
    // from the checkbox so same-tab links carry no target=_blank.
    saveLinkDialog() {
      const url = (this.linkDialog.url || '').trim()
      if (!url) {
        this.chain().extendMarkRange('link').unsetLink().run()
        this.closeLinkDialog()
        return
      }
      const attrs = this.linkDialog.newTab
        ? { href: url, target: '_blank', rel: NEW_TAB_REL }
        : { href: url, target: null, rel: SAME_TAB_REL }
      this.chain()
        .extendMarkRange('link')
        .setLink(attrs)
        .run()
      this.closeLinkDialog()
    },

    // Remove the link at the cursor (toolbar unlink + Mod-Shift-K). No-op
    // when the cursor is not on a link.
    removeLinkAtCursor() {
      if (!this.editor) return
      this.chain().extendMarkRange('link').unsetLink().run()
    },

    openImagePicker() {
      const input = this.$refs.imageInput
      if (input) {
        input.value = ''
        input.click()
      }
    },

    onImageInputChange(event) {
      const file = event?.target?.files?.[0]
      if (file) this.handleImageFile(file)
    },

    // Clipboard paste: if the payload carries an image file, intercept and
    // upload it; otherwise let TipTap handle the paste normally (return
    // false so ProseMirror continues its default behavior).
    handleClipboard(event) {
      const file = this.imageFromDataTransfer(event?.clipboardData)
      if (!file) return false
      event.preventDefault()
      this.handleImageFile(file)
      return true
    },

    handleDropEvent(event) {
      const file = this.imageFromDataTransfer(event?.dataTransfer)
      if (!file) return false
      event.preventDefault()
      this.handleImageFile(file)
      return true
    },

    imageFromDataTransfer(dt) {
      const files = dt?.files
      if (!files || files.length === 0) return null
      const file = files[0]
      if (file && file.type && file.type.startsWith('image/')) return file
      return null
    },

    // Validate -> upload -> insert. Exposed on the instance so the spec can
    // drive it directly (it is the seam the button, file input, paste, and
    // drop all funnel into).
    async handleImageFile(file) {
      this.uploadError = null
      // Respect the prototype-local inline-images flag on every entry path
      // (toolbar button, file input, paste, drop) so the disabled state is
      // honored consistently, not just on the button.
      if (!this.inlineImagesEnabled) {
        this.uploadError =
          'Inline images are disabled. Enable them in Text Editor settings.'
        return
      }
      const validationError = this.validateImage(file)
      if (validationError) {
        this.uploadError = validationError
        return
      }
      this.uploading = true
      try {
        const uuid = await this.uploadImage(file, file.name)
        if (!uuid) {
          this.uploadError = 'Upload succeeded but returned no image id.'
          return
        }
        const src = getImageBaseUrl() + uuid
        this.editor
          .chain()
          .focus()
          .setImage({ src, alt: file.name })
          .run()
        // setImage triggers onUpdate, but sync defensively for tests that
        // do not rely on the async event loop.
        this.html = this.editor.getHTML()
      } catch (e) {
        this.uploadError = this.errText(e)
      } finally {
        this.uploading = false
      }
    },

    validateImage(file) {
      if (!file) return 'Choose an image file.'
      if (!IMAGE_MIME_ALLOWLIST.includes(file.type)) {
        return 'Unsupported file type. Choose a PNG, JPEG, GIF, WebP, or SVG.'
      }
      if (file.size > IMAGE_MAX_BYTES) {
        return 'Image is too large. Maximum size is 10 MB.'
      }
      return null
    },

    // Mirrors productEditor.js _uploadArtwork exactly: multipart POST to
    // /api/upload_image, field name `file`, Content-Type unset so axios
    // derives the multipart boundary (the shared axios instance otherwise
    // forces application/vnd.api+json). Returns response.data.uuid.
    async uploadImage(file, filename) {
      const formData = new FormData()
      formData.append('file', file, filename)
      const response = await api.post('/api/upload_image', formData, {
      })
      return response?.data?.uuid
    },

    errText(e) {
      return (
        e?.response?.data?.error?.message ||
        e?.message ||
        'Upload failed'
      )
    },
  },
}
</script>

<style scoped>
.tiptap-proto {
  max-width: 1200px;
}
.tiptap-proto__title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 6px;
  color: #1a202c;
}
.tiptap-proto__sub {
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 24px;
  max-width: 72ch;
}
.tiptap-proto__sub code,
.tiptap-proto__hint code,
.tiptap-proto__output-note code,
.tiptap-proto__output-title code {
  background: #edf2f7;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 12px;
}
/* Prototype-local mock "Text Editor settings" card. Visually distinct
   (dashed accent border + "Prototype preview" badge) so it never reads as a
   real, persisted platform setting. */
.tiptap-proto__settings {
  border: 1px dashed #b794f4;
  background: #faf5ff;
  border-radius: 8px;
  padding: 14px 16px;
  margin: 0 0 24px;
  max-width: 520px;
}
.tiptap-proto__settings-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.tiptap-proto__settings-title {
  font-size: 14px;
  font-weight: 600;
  color: #44337a;
  margin: 0;
}
.tiptap-proto__settings-badge {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b46c1;
  background: #e9d8fd;
  border-radius: 4px;
  padding: 2px 7px;
}
.tiptap-proto__settings-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #2d3748;
}
.tiptap-proto__settings-label {
  user-select: none;
}
.tiptap-proto__settings-note {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin: 8px 0 0;
}
.tiptap-proto__body {
  display: flex;
  gap: 28px;
  align-items: flex-start;
}
.tiptap-proto__editor-col {
  flex: 1 1 55%;
  min-width: 0;
}
.tiptap-proto__output-col {
  flex: 1 1 45%;
  min-width: 0;
}
/* Conventional WYSIWYG toolbar: one cohesive bar sitting flush on top of
   the editor surface (shares its border, no gap), buttons visually grouped
   with vertical dividers between groups -- reads like the Google Docs /
   Word toolbar a user immediately recognizes. */
.tiptap-proto__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border: 1px solid #cbd5e0;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: #f7fafc;
}
/* Block-format dropdown at the left of the bar (Paragraph / Heading 2 /
   Heading 3). Styled to sit level with the icon buttons. */
.tiptap-proto__block-select {
  height: 30px;
  padding: 0 26px 0 10px;
  border: 1px solid #cbd5e0;
  border-radius: 5px;
  background: #fff;
  font-size: 13px;
  line-height: 1;
  color: #2d3748;
  cursor: pointer;
  /* Custom chevron so the control reads as an editor block-format menu
     across browsers, not a bare native select. */
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232d3748' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
}
.tiptap-proto__block-select:hover {
  background-color: #f7fafc;
}
.tiptap-proto__block-select:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}
/* Uniform square-ish icon-style buttons: borderless at rest, fill on hover,
   filled highlight when active. */
.tiptap-proto__tbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: transparent;
  font-size: 13px;
  line-height: 1;
  color: #2d3748;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}
/* Material Icons ligature glyphs sit inside the icon buttons at a
   toolbar-appropriate size. */
.tiptap-proto__tbtn .material-icons {
  font-size: 20px;
  line-height: 1;
}
.tiptap-proto__tbtn:hover:not(:disabled) {
  background: #e2e8f0;
  border-color: #cbd5e0;
}
.tiptap-proto__tbtn:active:not(:disabled) {
  background: #cbd5e0;
}
.tiptap-proto__tbtn:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}
/* Pressed / active state -- e.g. the H2 button when the cursor is in an H2. */
.tiptap-proto__tbtn.is-active {
  background: #2b6cb0;
  border-color: #2b6cb0;
  color: #fff;
}
.tiptap-proto__tbtn.is-active:hover:not(:disabled) {
  background: #2c5282;
  border-color: #2c5282;
}
.tiptap-proto__tbtn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
/* The Image button ends the bar as the one accent (upload) action. */
.tiptap-proto__tbtn--primary {
  color: #2f855a;
  font-weight: 600;
}
.tiptap-proto__tbtn--primary:hover:not(:disabled) {
  background: #c6f6d5;
  border-color: #9ae6b4;
}
/* Uploading cue: gently spin the hourglass so the disabled image button
   reads as busy rather than broken. */
.tiptap-proto__spin {
  animation: tiptap-proto-spin 1.2s linear infinite;
}
@keyframes tiptap-proto-spin {
  to {
    transform: rotate(360deg);
  }
}
/* Real divider lines between button groups. */
.tiptap-proto__sep {
  width: 1px;
  align-self: stretch;
  min-height: 22px;
  background: #cbd5e0;
  margin: 4px 5px;
}
.tiptap-proto__surface {
  border: 1px solid #cbd5e0;
  border-radius: 0 0 8px 8px;
  min-height: 280px;
  padding: 14px 16px;
  background: #fff;
}
.tiptap-proto__surface :deep(.ProseMirror) {
  outline: none;
  min-height: 250px;
}
.tiptap-proto__surface :deep(.ProseMirror:focus) {
  outline: none;
}
.tiptap-proto__surface :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}
.tiptap-proto__surface :deep(blockquote) {
  border-left: 3px solid #cbd5e0;
  margin: 8px 0;
  padding-left: 12px;
  color: #4a5568;
}
.tiptap-proto__file {
  display: none;
}
.tiptap-proto__error {
  color: #c53030;
  font-size: 13px;
  margin: 10px 0 0;
}
.tiptap-proto__hint {
  font-size: 12px;
  color: var(--tf-fg-4);
  line-height: 1.6;
  margin: 12px 0 0;
}
.tiptap-proto__output-head {
  margin-bottom: 8px;
}
.tiptap-proto__output-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 4px;
}
.tiptap-proto__output-note {
  font-size: 12px;
  color: var(--tf-fg-4);
  line-height: 1.5;
  margin: 0;
}
.tiptap-proto__output {
  margin: 0;
  padding: 14px 16px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  background: #1a202c;
  color: #e2e8f0;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 280px;
  max-height: 520px;
  overflow: auto;
}

/* Link editor dialog: a centered modal panel over a dimming overlay. Plain
   Vue + scoped CSS -- no dialog dependency, matching the app's neutral
   slate palette used elsewhere on this page. */
.tiptap-proto__dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 32, 44, 0.45);
  padding: 20px;
}
.tiptap-proto__dialog {
  width: 100%;
  max-width: 380px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  padding: 20px 22px;
}
.tiptap-proto__dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 14px;
}
.tiptap-proto__dialog-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  margin: 0 0 6px;
}
.tiptap-proto__dialog-input {
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
  color: #1a202c;
}
.tiptap-proto__dialog-input:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}
.tiptap-proto__dialog-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #2d3748;
  margin: 14px 0 0;
  cursor: pointer;
}
.tiptap-proto__dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
.tiptap-proto__dialog-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  cursor: pointer;
}
.tiptap-proto__dialog-btn:hover {
  background: #f7fafc;
}
.tiptap-proto__dialog-btn:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}
.tiptap-proto__dialog-btn--primary {
  background: #2b6cb0;
  border-color: #2b6cb0;
  color: #fff;
}
.tiptap-proto__dialog-btn--primary:hover {
  background: #2c5282;
  border-color: #2c5282;
}
</style>
